if (!customElements.get('segastore-sport-mark')) {
  customElements.define('segastore-sport-mark', class SegastoreSportMark extends HTMLElement {
    connectedCallback() {
      this.sequence = ['logo', 'badminton', 'logo', 'football', 'logo', 'boxing', 'logo', 'running'];
      this.index = 0;
      this.token = 0;
      this.logoDuration = 7600;
      this.sportDuration = 3000;
      this.phaseTimers = [];
      this.mediaUnavailable = false;
      this.paused = false;
      this.focused = false;
      this.hovered = false;
      this.scenes = [...this.querySelectorAll('[data-sport-scene]')];
      this.logoStage = this.querySelector('[data-logo-stage]');
      this.failedScenes = new Set();
      this.images = new Map();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce), (max-width: 749px)');
      this.clock = window.segastoreMotionClock;
      this.dataset.clockFrame = String(Boolean(this.clock));
      this.brand = this.closest('.segastore-brand');
      this.buttons = [...document.querySelectorAll('[data-sport-mark-toggle]')];
      this.handleToggle = () => {
        this.paused = !this.paused;
        this.sync();
      };
      this.handleMotion = () => this.sync();
      this.handleVisibility = () => this.sync();
      this.handleEnter = (event) => {
        if (event.pointerType === 'mouse') {
          this.hovered = true;
          this.sync();
        }
      };
      this.handleLeave = (event) => {
        if (event.pointerType === 'mouse') {
          this.hovered = false;
          this.sync();
        }
      };
      this.handleFocus = () => {
        this.focused = true;
        this.sync();
      };
      this.handleBlur = (event) => {
        this.focused = this.brand?.contains(event.relatedTarget) || false;
        this.sync();
      };
      this.buttons.forEach((button) => button.addEventListener('click', this.handleToggle));
      this.motionQuery.addEventListener('change', this.handleMotion);
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.brand?.addEventListener('pointerenter', this.handleEnter);
      this.brand?.addEventListener('pointerleave', this.handleLeave);
      this.brand?.addEventListener('focusin', this.handleFocus);
      this.brand?.addEventListener('focusout', this.handleBlur);
      this.inViewport = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inViewport = entries.some((entry) => entry.isIntersecting);
          this.sync();
        });
        this.observer.observe(this);
      }
      this.unsubscribeClock = this.clock?.subscribe({
        running: () => this.running(),
        advance: (now, framePhase) => this.renderFrame(framePhase % 6)
      });
      this.sync();
    }

    disconnectedCallback() {
      this.unsubscribeClock?.();
      this.stop();
      this.observer?.disconnect();
      this.buttons?.forEach((button) => button.removeEventListener('click', this.handleToggle));
      this.motionQuery?.removeEventListener('change', this.handleMotion);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.brand?.removeEventListener('pointerenter', this.handleEnter);
      this.brand?.removeEventListener('pointerleave', this.handleLeave);
      this.brand?.removeEventListener('focusin', this.handleFocus);
      this.brand?.removeEventListener('focusout', this.handleBlur);

    }

    running() {
      return !this.mediaUnavailable && this.scenes.length > 0 && !this.paused && !this.focused && !this.hovered && !this.motionQuery.matches && !document.hidden && this.inViewport;
    }

    stop() {
      this.token += 1;
      window.cancelAnimationFrame(this.logoFrame);
      this.logoFrame = undefined;
      window.clearTimeout(this.timer);
      window.clearTimeout(this.loadTimer);
      this.phaseTimers?.forEach((timer) => window.clearTimeout(timer));
      this.phaseTimers = [];
      this.timer = undefined;
      this.loadTimer = undefined;
      this.scenes?.forEach((scene) => scene.classList.remove('is-active'));
      this.dataset.scene = 'logo';
      this.dataset.logoActive = 'false';
      this.dataset.logoPhase = 'static';
    }

    updateControls() {
      this.buttons.forEach((button) => {
        button.hidden = this.motionQuery.matches || this.mediaUnavailable || this.scenes.length === 0;
        const paused = this.paused;
        button.setAttribute('aria-pressed', String(paused));
        const label = paused ? button.dataset.playLabel : button.dataset.pauseLabel;
        if (label) {
          button.setAttribute('aria-label', label);
          button.setAttribute('title', label);
          const text = button.querySelector('[data-sport-mark-label]');
          if (text) text.textContent = label;
          else if (button.classList.contains('segastore-footer-motion')) button.textContent = label;
        }
      });
    }

    sync() {
      this.stop();
      this.dataset.motion = this.running() ? 'running' : 'paused';
      this.updateControls();
      if (this.running()) this.startSlot();
      this.clock?.schedule();
    }

    renderFrame(frame) {
      const scene = this.scenes.find((element) => element.classList.contains('is-active'));
      if (!scene) return;
      scene.querySelectorAll('.segastore-sport-mark__frame').forEach((element, index) => element.classList.toggle('is-current', index === frame));
      this.dataset.frame = String(frame);
    }

    slotDelay(duration) {
      if (!this.clock) return duration;
      const now = performance.now();
      return Math.ceil((now + duration) / this.clock.galleryInterval) * this.clock.galleryInterval - now;
    }

    advance() {
      this.stop();
      this.index = (this.index + 1) % this.sequence.length;
      if (this.running()) this.startSlot();
    }

    loadScene(scene) {
      const name = scene.dataset.sportScene;
      if (this.images.has(name)) return this.images.get(name);
      const loading = new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          scene.style.setProperty('--sport-sheet', `url(${JSON.stringify(scene.dataset.src)})`);
          scene.style.setProperty('--sport-frame-ratio', String((image.naturalWidth / image.naturalHeight) * (2 / 3)));
          scene.dataset.sheetWidth = String(image.naturalWidth);
          scene.dataset.sheetHeight = String(image.naturalHeight);
          scene.dataset.ready = 'true';
          resolve(scene);
        };
        image.onerror = () => {
          this.failedScenes.add(name);
          if (this.failedScenes.size === this.scenes.length) this.mediaUnavailable = true;
          reject(new Error('Sport scene unavailable'));
        };
        image.src = scene.dataset.src;
      });
      this.images.set(name, loading);
      return loading;
    }

    startSlot() {
      const name = this.sequence[this.index];
      if (name === 'logo') {
        const token = this.token;
        const duration = this.slotDelay(this.logoDuration);
        const dissolveStart = duration - 3900;
        this.style.setProperty('--sport-logo-dissolve-start', `${dissolveStart}ms`);
        this.dataset.scene = 'logo';
        this.dataset.logoActive = 'false';
        this.logoFrame = window.requestAnimationFrame(() => {
          if (token !== this.token || !this.running() || !this.isConnected) return;
          this.logoFrame = window.requestAnimationFrame(() => {
            this.logoFrame = undefined;
            if (token !== this.token || !this.running() || !this.isConnected) return;
            this.dataset.logoActive = 'true';
            this.dataset.logoPhase = 'assembling';
            this.phaseTimers = [
              window.setTimeout(() => {
                if (token !== this.token || !this.running()) return;
                this.dataset.logoPhase = 'hold';
                this.prewarmNext();
              }, 2200),
              window.setTimeout(() => {
                if (token === this.token && this.running()) this.dataset.logoPhase = 'dissolving';
              }, dissolveStart)
            ];
            this.timer = window.setTimeout(() => this.advance(), duration);
          });
        });
        return;
      }
      const scene = this.scenes.find((element) => element.dataset.sportScene === name);
      if (!scene || this.failedScenes.has(name)) {
        this.advance();
        return;
      }
      const token = this.token;
      this.loadTimer = window.setTimeout(() => {
        if (token === this.token && this.running()) this.advance();
      }, 4000);
      this.loadScene(scene).then(() => {
        if (token !== this.token || !this.running()) return;
        window.clearTimeout(this.loadTimer);
        this.loadTimer = undefined;
        scene.classList.add('is-active');
        this.dataset.scene = name;
        this.clock?.schedule();
        this.timer = window.setTimeout(() => this.advance(), this.slotDelay(this.sportDuration));
      }).catch(() => {
        if (token !== this.token) return;
        if (this.mediaUnavailable) this.sync();
        else if (this.running()) this.advance();
      });
    }

    prewarmNext() {
      const name = this.sequence[(this.index + 1) % this.sequence.length];
      const scene = this.scenes.find((element) => element.dataset.sportScene === name);
      if (!scene || this.failedScenes.has(name)) return;
      const token = this.token;
      this.loadScene(scene).catch(() => {
        if (token === this.token && this.mediaUnavailable) this.sync();
      });
    }
  });
}
